import { withWindow } from '@shared/utils/withWindow';

const localeToCurrency: Record<typeof navigator.language, string> = {
  'en-US': 'USD',
  'en-GB': 'GBP',
  'en-CA': 'CAD',
  'fr-CA': 'CAD',
  'fr-FR': 'EUR',
  'de-DE': 'EUR',
  'es-ES': 'EUR',
  'ja-JP': 'JPY',
  'zh-CN': 'CNY',
  'ko-KR': 'KRW',
  'pt-BR': 'BRL',
  'ru-RU': 'RUB',
  'hi-IN': 'INR',
  'en-AU': 'AUD',
  'en-NZ': 'NZD',
  'en-IN': 'INR',
  'en-ZA': 'ZAR',
  'ar-AE': 'AED',
  'en-FJ': 'FJD',
  'en-SG': 'SGD',
  'en-PH': 'PHP',
  'en-MY': 'MYR',
  'en-OM': 'OMR',
  'en-SA': 'SAR',
  'en-HK': 'HKD',
  'en-MU': 'MUR',
  'en-MX': 'MXN',
  'en-LK': 'LKR',
  'en-MN': 'MNT',
  'en-MA': 'MAD',
  'en-ID': 'IDR',
  'en-TH': 'THB',
  'en-TW': 'TWD',
  'en-JE': 'GBP',
  'en-GG': 'GBP',
  'en-IM': 'GBP',
  'en-IE': 'EUR',
  'en-NF': 'AUD',
  'en-TV': 'AUD',
  'en-KI': 'AUD',
  'en-NR': 'AUD',
  'en-CC': 'AUD',
  'en-CX': 'AUD',
  'en-HM': 'AUD',
  'en-PT': 'EUR',
  'en-NL': 'EUR',
  'en-DE': 'EUR',
  'en-IT': 'EUR',
  'en-ES': 'EUR',
  'en-FI': 'EUR',
  'en-GR': 'EUR',
  'en-AT': 'EUR',
  'en-BE': 'EUR',
  'en-CY': 'EUR',
  'en-EE': 'EUR',
  'en-LV': 'EUR',
  'en-LT': 'EUR',
  'en-LU': 'EUR',
  'en-MT': 'EUR',
  'en-SK': 'EUR',
  'en-SI': 'EUR',
  'en-MC': 'EUR',
  'en-ME': 'EUR',
  'en-SM': 'EUR',
  'en-AX': 'EUR',
  'en-AD': 'EUR',
  'en-VA': 'EUR',
  'en-BL': 'EUR',
  'en-MF': 'EUR',
  'en-PM': 'EUR',
  'en-RE': 'EUR',
  'en-YT': 'EUR',
  'en-GP': 'EUR',
  'en-MQ': 'EUR',
  'en-TF': 'EUR',
  'en-GF': 'EUR',
  'en-LS': 'ZAR',
  'en-NA': 'ZAR',
  'en-BT': 'INR',
  'en-AS': 'USD',
  'en-BQ': 'USD',
  'en-IO': 'USD',
  'en-EC': 'USD',
  'en-SV': 'USD',
  'en-GU': 'USD',
  'en-HT': 'USD',
  'en-MH': 'USD',
  'en-FM': 'USD',
  'en-MP': 'USD',
  'en-PW': 'USD',
  'en-PA': 'USD',
  'en-PR': 'USD',
  'en-TL': 'USD',
  'en-TC': 'USD',
  'en-UM': 'USD',
  'en-VG': 'USD',
  'en-VI': 'USD'
} as const;

type LanguageCode = typeof navigator.language;
type CurrencyCode = typeof localeToCurrency[LanguageCode];
type CurrencyResponse = {
  code: CurrencyCode | undefined;
  error: Error | undefined;
};

const codeFromLocale = (locale: LanguageCode) => {
  if (localeToCurrency[locale]) {
    return {
      code: localeToCurrency[locale],
      locale,
      error: undefined
    };
  }

  // Try country code only
  const countryCode = locale.split('-')[1];
  if (countryCode) {
    for (const key in localeToCurrency) {
      if (key.endsWith(countryCode)) {
        return {
          code: localeToCurrency[key],
          locale,
          error: undefined
        };
      }
    }
  }

  // Try base language only
  const baseLang = locale.split('-')[0];
  for (const key in localeToCurrency) {
    if (key.startsWith(baseLang)) {
      return {
        code: localeToCurrency[key],
        locale,
        error: undefined
      };
    }
  }
  return {
    code: undefined,
    locale,
    error: new Error(`No currency code found for ${locale}`)
  };
};

/**
 * Only for use by hotels, it's not in-line with good practices
 * This is meant as a stop-gap solution to gage impact of "correct" currency on click-through
 *
 * see discussion https://github.com/joylifeinc/joy-web/pull/9972/files#r2067505589
 *
 * see proposal https://withjoy.atlassian.net/wiki/x/AoDG5
 */
export const useHotelCurrencyCode = withWindow(
  global => (locale: LanguageCode = global.navigator.language): CurrencyResponse => {
    return codeFromLocale(locale);
  },
  (locale?: LanguageCode): CurrencyResponse => {
    return codeFromLocale(locale || 'en-US');
  }
);
